import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { useTranslation } from 'react-i18next';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Select from '../../components/common/Select';
import { ArrowLeft, BadgeInfo, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const formatDateTime = (value, locale) => {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return new Intl.DateTimeFormat(locale || undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }).format(d);
  } catch {
    return String(value);
  }
};

export default function UserDetail() {
  const { userId } = useParams();
  const { t, i18n } = useTranslation();
  const { getUserById, getUserSubscriptions, plans, fetchPlans, assignUserPlan, updateUser } = useAdmin();

  const [user, setUser] = useState(null);
  const [subs, setSubs] = useState({ active: null, history: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [planId, setPlanId] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const [u, s] = await Promise.all([
        getUserById(userId),
        getUserSubscriptions(userId),
      ]);
      if (mounted) {
        if (u?.success) {
          const data = u.data?.data || u.data; // controller wraps under data
          setUser(data);
          setRole(data?.role || 'CUSTOMER');
          setEnabled(Boolean(data?.enabled ?? true));
        }
        if (s?.success) {
          const d = s.data?.data || s.data;
          setSubs({
            active: d?.active || null,
            history: Array.isArray(d?.history) ? d.history : [],
          });
        }
        await fetchPlans();
        setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [userId, getUserById, getUserSubscriptions, fetchPlans]);

  const active = subs.active;
  const history = subs.history || [];

  const handleAssignPlan = async () => {
    if (!planId) return toast.error(t('admin.userDetail.plan.selectFirst'));
    const res = await assignUserPlan(userId, planId);
    if (res?.success) {
      toast.success(t('admin.userDetail.plan.assigned'));
      const s = await getUserSubscriptions(userId);
      if (s?.success) {
        const d = s.data?.data || s.data;
        setSubs({ active: d?.active || null, history: Array.isArray(d?.history) ? d.history : [] });
      }
    } else {
      toast.error(res?.error || t('admin.userDetail.plan.assignFailed'));
    }
  };

  const handleSaveUser = async () => {
    if (!user) return;
    setSaving(true);
    const res = await updateUser(user.id, { role, enabled });
    setSaving(false);
    if (res?.success) toast.success(t('admin.userDetail.saved')); else toast.error(res?.error || t('common.error'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('admin.userDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
        <div className="max-w-7xl mx-auto text-gray-200">{t('admin.userDetail.notFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/users" className="text-gray-300 hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t('admin.userDetail.back')}
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-white">{t('admin.userDetail.title')}</h1>
          </div>
          <Button onClick={handleSaveUser} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>

        {/* Overview */}
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-white font-semibold mb-2">{t('admin.userDetail.user')}</h3>
              <div className="text-gray-300">{user.firstName} {user.lastName}</div>
              <div className="text-gray-400 text-sm">{user.email}</div>
              <div className="mt-3 text-gray-400 text-sm">{t('admin.userDetail.joined')}: {formatDateTime(user.createdAt, i18n.language)}</div>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">{t('admin.userDetail.account')}</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 w-28">{t('admin.userDetail.role')}</span>
                  <Select value={role} onChange={(e) => setRole(e.target.value)}>
                    <option value="ADMIN">{t('admin.userManagement.roles.admin')}</option>
                    <option value="OWNER">{t('admin.userManagement.roles.owner')}</option>
                    <option value="BARISTA">{t('admin.userManagement.roles.barista')}</option>
                    <option value="CUSTOMER">{t('admin.userManagement.roles.customer')}</option>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 w-28">{t('admin.userDetail.enabled')}</span>
                  <Select value={String(enabled)} onChange={(e) => setEnabled(e.target.value === 'true')}>
                    <option value="true">{t('admin.userManagement.status.enabled')}</option>
                    <option value="false">{t('admin.userManagement.status.disabled')}</option>
                  </Select>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">{t('admin.userDetail.plan.manage')}</h3>
              <div className="flex gap-2">
                <Select value={planId} onChange={(e) => setPlanId(e.target.value)}>
                  <option value="">{t('admin.userDetail.plan.select')}</option>
                  {Array.isArray(plans) && plans.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.billingInterval?.toLowerCase?.() || p.billingInterval})</option>
                  ))}
                </Select>
                <Button onClick={handleAssignPlan}>{t('admin.userDetail.plan.assign')}</Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Active subscription */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">{t('admin.userDetail.activeSub')}</h3>
            <Button variant="secondary" onClick={async () => {
              const s = await getUserSubscriptions(userId);
              if (s?.success) {
                const d = s.data?.data || s.data;
                setSubs({ active: d?.active || null, history: Array.isArray(d?.history) ? d.history : [] });
                toast.success(t('common.refreshed'));
              }
            }}>
              <RefreshCw className="w-4 h-4 mr-2" /> {t('common.refresh')}
            </Button>
          </div>
          {active ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-gray-200">
              <div>
                <div className="text-gray-400 text-sm">{t('admin.userDetail.plan.plan')}</div>
                <div className="text-white font-medium">{active.plan?.name} <span className="text-gray-400">({active.plan?.billingInterval?.toLowerCase?.() || active.plan?.billingInterval})</span></div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">{t('admin.userDetail.plan.status')}</div>
                <div className="text-white font-medium">{active.status}</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">{t('admin.userDetail.plan.activatedAt')}</div>
                <div className="text-white font-medium">{formatDateTime(active.activatedAt, i18n.language)}</div>
              </div>
              <div>
                <div className="text-gray-400 text-sm">{t('admin.userDetail.plan.expiresAt')}</div>
                <div className="text-white font-medium">{formatDateTime(active.expiresAt, i18n.language)}</div>
              </div>
            </div>
          ) : (
            <div className="text-gray-400 flex items-center gap-2"><BadgeInfo className="w-4 h-4" /> {t('admin.userDetail.noActive')}</div>
          )}
        </Card>

        {/* Subscription History */}
        <Card>
          <h3 className="text-white font-semibold mb-4">{t('admin.userDetail.history')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 text-gray-400 text-sm">
                  <th className="text-left py-2 px-3">{t('admin.userDetail.plan.plan')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.plan.status')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.plan.activatedAt')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.plan.expiresAt')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.plan.cancelledAt')}</th>
                </tr>
              </thead>
              <tbody>
                {history.length ? history.map(item => (
                  <tr key={item.id} className="border-b border-gray-800 text-gray-200">
                    <td className="py-2 px-3">{item.plan?.name} <span className="text-gray-400">({item.plan?.billingInterval?.toLowerCase?.() || item.plan?.billingInterval})</span></td>
                    <td className="py-2 px-3">{item.status}</td>
                    <td className="py-2 px-3">{formatDateTime(item.activatedAt, i18n.language)}</td>
                    <td className="py-2 px-3">{formatDateTime(item.expiresAt, i18n.language)}</td>
                    <td className="py-2 px-3">{formatDateTime(item.cancelledAt, i18n.language)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td className="py-3 px-3 text-gray-400" colSpan={5}>{t('admin.userDetail.noHistory')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Payment History */}
        <Card>
          <h3 className="text-white font-semibold mb-4">{t('admin.userDetail.paymentHistory')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 text-gray-400 text-sm">
                  <th className="text-left py-2 px-3">{t('admin.userDetail.payment.date')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.payment.plan')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.payment.amount')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.payment.method')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.payment.status')}</th>
                  <th className="text-left py-2 px-3">{t('admin.userDetail.payment.id')}</th>
                </tr>
              </thead>
              <tbody>
                {history.filter(item => item.amountPaid && item.amountPaid > 0).length ? (
                  history.filter(item => item.amountPaid && item.amountPaid > 0).map(item => (
                    <tr key={item.id} className="border-b border-gray-800 text-gray-200">
                      <td className="py-2 px-3">{formatDateTime(item.activatedAt, i18n.language)}</td>
                      <td className="py-2 px-3">{item.plan?.name}</td>
                      <td className="py-2 px-3 font-medium">
                        {item.amountPaid?.toFixed(2)} {item.currency?.toUpperCase() || 'USD'}
                      </td>
                      <td className="py-2 px-3">
                        {item.paymentMethod ? (
                          <span className="capitalize">{item.paymentMethod}</span>
                        ) : (
                          <span className="text-gray-500">{t('admin.userDetail.payment.noMethod')}</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.status === 'ACTIVE' ? 'bg-green-900/30 text-green-400' :
                          item.status === 'EXPIRED' ? 'bg-gray-700 text-gray-400' :
                          item.status === 'CANCELLED' ? 'bg-red-900/30 text-red-400' :
                          'bg-yellow-900/30 text-yellow-400'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-400">
                        {item.stripePaymentId ? (
                          <span title={item.stripePaymentId}>{item.stripePaymentId.substring(0, 20)}...</span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="py-3 px-3 text-gray-400" colSpan={6}>{t('admin.userDetail.noPayments')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ============================================ */}
        {/* TESTING SECTION - Remove after testing */}
        {/* ============================================ */}
        {active && (
          <Card className="border-2 border-yellow-600 bg-yellow-900/10">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">🧪</span>
              <h3 className="text-yellow-400 font-semibold">TEST: Manually Change Expiry Date</h3>
            </div>
            <p className="text-yellow-300 text-sm mb-4">
              ⚠️ This is a testing tool. Use it to test subscription enforcement by making the subscription expired or active.
            </p>
            <TestExpiryChanger subscriptionId={active.id} currentExpiry={active.expiresAt} onUpdate={() => {
              getUserSubscriptions(userId).then(s => {
                if (s?.success) {
                  const d = s.data?.data || s.data;
                  setSubs({ active: d?.active || null, history: Array.isArray(d?.history) ? d.history : [] });
                }
              });
            }} />
          </Card>
        )}
      </div>
    </div>
  );
}

// ============================================
// TEST COMPONENT - Remove after testing
// ============================================
function TestExpiryChanger({ subscriptionId, currentExpiry, onUpdate }) {
  const { updateSubscriptionExpiry } = useAdmin();
  const [newExpiry, setNewExpiry] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (currentExpiry) {
      // Convert to input format (yyyy-MM-ddTHH:mm)
      const date = new Date(currentExpiry);
      const formatted = date.toISOString().slice(0, 16);
      setNewExpiry(formatted);
    }
  }, [currentExpiry]);

  const handleUpdate = async () => {
    if (!newExpiry) {
      toast.error('Please select a date');
      return;
    }

    setUpdating(true);
    try {
      // Add seconds to the date string
      const fullDateString = newExpiry + ':00';
      const result = await updateSubscriptionExpiry(subscriptionId, fullDateString);
      
      if (result.success) {
        toast.success('✅ Expiry date updated successfully!');
        onUpdate();
      } else {
        toast.error(result.error || 'Failed to update expiry date');
      }
    } catch (error) {
      toast.error('Error updating expiry date: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const setExpiredYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setNewExpiry(yesterday.toISOString().slice(0, 16));
  };

  const setActiveNextMonth = () => {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setNewExpiry(nextMonth.toISOString().slice(0, 16));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-gray-400 text-sm mb-2">New Expiry Date</label>
          <input
            type="datetime-local"
            value={newExpiry}
            onChange={(e) => setNewExpiry(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-yellow-500"
          />
        </div>
        <div className="flex items-end gap-2">
          <Button
            onClick={setExpiredYesterday}
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500/10"
          >
            Set Expired (Yesterday)
          </Button>
        </div>
        <div className="flex items-end gap-2">
          <Button
            onClick={setActiveNextMonth}
            variant="outline"
            className="border-green-500 text-green-400 hover:bg-green-500/10"
          >
            Set Active (Next Month)
          </Button>
        </div>
      </div>
      
      <Button
        onClick={handleUpdate}
        disabled={updating}
        className="bg-yellow-600 hover:bg-yellow-700"
      >
        {updating ? '⏳ Updating...' : '💾 Update Expiry Date'}
      </Button>

      <div className="bg-gray-800/50 p-3 rounded text-sm">
        <p className="text-gray-300 mb-2"><strong>Quick Test Guide:</strong></p>
        <ul className="text-gray-400 space-y-1 list-disc list-inside">
          <li><strong className="text-red-400">Set Expired:</strong> Makes subscription expired - should get 402 errors on business operations</li>
          <li><strong className="text-green-400">Set Active:</strong> Makes subscription active - all features should work</li>
          <li>After changing, test endpoints like /shop, /orders, /analytics to verify enforcement</li>
          <li>Plans and payment pages should ALWAYS be accessible even when expired</li>
        </ul>
      </div>
    </div>
  );
}
